/**
 * VODLinkForm - Simple form for adding a VOD link (direct MP4/video URL)
 * Lightweight alternative to GameRecordingForm when you just want to paste a video link.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { Link2, X, ExternalLink } from "lucide-react";
import { Button } from "../../ui/button";
import { ImageUpload } from "../ImageUpload";

const SPORTS_LIST = [
  "Football", "Basketball", "Boys Basketball", "Girls Basketball",
  "Mens Basketball", "Womens Basketball", "Soccer", "Baseball",
  "Softball", "Volleyball", "Wrestling", "Tennis", "Golf",
  "Track & Field", "Swimming", "Lacrosse", "Hockey"
];

export const VODLinkForm = ({ onSave, onClose, saving }) => {
  const [form, setForm] = useState({
    video_url: "",
    title: "",
    sport: "Football",
    recorded_at: new Date().toISOString().split("T")[0],
    thumbnail_url: "",
    description: "",
    is_published: true,
  });

  const handleSave = () => {
    if (!form.video_url.trim()) {
      alert("Please paste a video URL.");
      return;
    }
    if (!form.title.trim()) {
      // Auto-generate title from URL filename
      try {
        const url = new URL(form.video_url);
        const pathParam = url.searchParams.get("path");
        const path = pathParam || url.pathname;
        const filename = path.split("/").pop().replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ");
        form.title = filename || "VOD Recording";
      } catch {
        form.title = "VOD Recording";
      }
    }
    onSave(form);
  };

  const isValidUrl = (url) => {
    try { new URL(url); return true; } catch { return false; }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 bg-zinc-900/50 border border-cyan-500/30 space-y-5"
      data-testid="vod-link-form"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link2 className="w-5 h-5 text-cyan-400" />
          <div>
            <h3 className="font-secondary text-lg font-bold uppercase text-white">Add VOD Link</h3>
            <p className="text-xs text-zinc-500">Paste any direct video URL (MP4, stream link, etc.)</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* VOD URL */}
      <div className="bg-cyan-500/10 border border-cyan-500/30 p-4">
        <label className="block text-xs font-bold uppercase tracking-widest text-cyan-400 mb-2">
          Video URL *
        </label>
        <input
          type="text"
          value={form.video_url}
          onChange={(e) => setForm({ ...form, video_url: e.target.value })}
          placeholder="https://antlersn.tv/api/staff/media/file-manager/preview?path=%2FTKDS_STREAMS%2F..."
          className="w-full bg-zinc-800 border border-cyan-500/50 text-white px-3 py-3 text-sm font-mono"
          data-testid="vod-url-input"
        />
        <p className="text-[10px] text-zinc-600 mt-1.5">
          Accepts any direct video URL — MP4, HLS, or stream preview links with query parameters.
        </p>
        {form.video_url && isValidUrl(form.video_url) && (
          <a href={form.video_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] text-cyan-500 hover:text-cyan-400 mt-1">
            <ExternalLink className="w-3 h-3" /> Preview link
          </a>
        )}
      </div>

      {/* Title + Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Title</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Auto-generated from filename if left blank"
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="vod-title-input"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Sport</label>
          <select
            value={form.sport}
            onChange={(e) => setForm({ ...form, sport: e.target.value })}
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          >
            {SPORTS_LIST.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Date</label>
          <input
            type="date"
            value={form.recorded_at}
            onChange={(e) => setForm({ ...form, recorded_at: e.target.value })}
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Description (optional)</label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Optional notes or description..."
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Thumbnail */}
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Thumbnail (optional)</label>
        <ImageUpload
          value={form.thumbnail_url}
          onChange={(url) => setForm({ ...form, thumbnail_url: url })}
          label=""
          placeholder="Thumbnail URL or upload"
          previewSize="lg"
          id="vod-thumbnail"
        />
      </div>

      {/* Publish toggle */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="vod_is_published"
          checked={form.is_published}
          onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
          className="w-4 h-4"
        />
        <label htmlFor="vod_is_published" className="text-sm text-zinc-400">
          Publish immediately to On Demand page
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
        <Button
          type="button"
          onClick={onClose}
          className="bg-zinc-800 text-white hover:bg-zinc-700 font-secondary text-xs px-6 py-2 h-auto rounded-none"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="bg-cyan-600 text-white hover:bg-cyan-700 font-secondary font-bold text-xs px-6 py-2 h-auto rounded-none"
          data-testid="save-vod-link-btn"
        >
          {saving ? "Saving..." : "Add VOD"}
        </Button>
      </div>
    </motion.div>
  );
};

export default VODLinkForm;
